import * as React from 'react';

import type { HTMLFocusableElement } from 'types/common_types';
import Button from 'components/shared/Button';

const { useEffect, useRef, useState } = React;

export type ModalProps = {
  children?: React.ReactNode;
  isAlert?: boolean;
  modalClass?: string[];
  pageStartIndex?: number | null;
  primaryFooterGroup?: JSX.Element | null;
  secondaryFooterGroup?: JSX.Element | null;
  /** a11y label for the dialog role element */
  labelledBy: string;
  /** ID of element which triggered modal opening */
  lastFocusedElementId: string;
  /** Title text for modal header */
  title: string;
  onClose: () => void;
};

function Modal({
  children = '',
  isAlert = false,
  modalClass = [],
  pageStartIndex = null,
  primaryFooterGroup = null,
  secondaryFooterGroup = null,
  labelledBy,
  lastFocusedElementId,
  onClose,
  title,
}: ModalProps) {
  const container = useRef<HTMLDivElement>(null);

  const [pages, setPages] = useState<JSX.Element[]>([]);

  const closeButtonId = `modal-close-${labelledBy}`;

  function registerEscape(event: KeyboardEvent) {
    const focusEl = document.getElementById(lastFocusedElementId);
    if (event.key === 'Esc' || event.key === 'Escape') {
      onClose();
      if (focusEl) setTimeout(() => focusEl.focus(), 5);
    }
  }

  function containFocus(event: FocusEvent) {
    const containerEl = container.current;
    const target = event.target as HTMLElement;

    if (containerEl !== null && !containerEl.contains(target)) {
      event.stopPropagation();
      // move back up to the close button, which will flow focus back to the rest of the modal
      containerEl.querySelector<HTMLButtonElement>('#modal-close')?.focus();
    }
  }

  function setFirstFocus(containerEl: HTMLDivElement | null) {
    if (containerEl === null) return;

    function findFocusableElement(el: HTMLDivElement | null): HTMLFocusableElement | null {
      if (el === null) return null;

      return el.querySelector('a, input, button, select, textarea');
    }

    const bodyFocusableElement = findFocusableElement(containerEl.querySelector('.modal-body'));
    const focusableElement =
      bodyFocusableElement || findFocusableElement(containerEl.querySelector('.modal-footer'));

    if (focusableElement) {
      focusableElement.focus();
    } else {
      containerEl.querySelector<HTMLButtonElement>('#modal-close')?.focus();
    }
  }

  function closeIfOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.className === 'cp-modal') {
      onClose();
    }
  }

  function handleModalClose() {
    document.removeEventListener('keydown', registerEscape, true);
    document.removeEventListener('focus', containFocus, true);
    document.removeEventListener('click', closeIfOutside, true);

    const focusEl = document.getElementById(lastFocusedElementId);
    if (focusEl) setTimeout(() => focusEl.focus(), 5);
    onClose();
  }

  function moveLeft() {
    setPages((prev) => {
      const next = [...prev];
      const el = next.pop();
      if (el) {
        next.unshift(el);
      }
      return next;
    });
  }

  function moveRight() {
    setPages((prev) => {
      const next = [...prev];
      const el = next.shift();
      if (el) {
        next.push(el);
      }
      return next;
    });
  }

  // Init the pages array if children change
  useEffect(() => {
    if (pageStartIndex !== null) {
      const arr = [...(children as JSX.Element[])];
      const untilStart = arr.splice(0, pageStartIndex);
      setPages(arr.concat(untilStart));
    }
  }, [children, pageStartIndex]);

  useEffect(() => {
    document.addEventListener('focus', containFocus, true);
    setFirstFocus(container.current);

    return function cleanup() {
      document.removeEventListener('focus', containFocus, true);
    };
  }, []);

  // If the prop to close the modal changed, re-bind the event listeners for close
  useEffect(() => {
    document.removeEventListener('keydown', registerEscape, true);
    document.removeEventListener('click', closeIfOutside, true);
    document.addEventListener('keydown', registerEscape, true);
    document.addEventListener('click', closeIfOutside, true);

    return function cleanup() {
      document.removeEventListener('keydown', registerEscape, true);
      document.removeEventListener('click', closeIfOutside, true);
    };
  }, [onClose]);

  const classText = ['modal-content', ...modalClass];

  const hasFooter = !!primaryFooterGroup || !!secondaryFooterGroup;

  return (
    <div
      className={`modal ${isAlert ? 'alert' : ''}`}
      role="dialog"
      aria-labelledby={labelledBy}
      ref={container}
    >
      <div className="cp-modal">
        <div className={classText.join(' ')}>
          <div className="modal-header">
            <h1 id={labelledBy} className="modal-title">
              {title}
            </h1>
            <Button
              a11yText="Close Dialog"
              id={closeButtonId}
              className="modal-close"
              onClick={handleModalClose}
              icon="times"
              type="icon"
              size="lg"
            />
          </div>
          <div className="modal-body">
            {pageStartIndex === null ? (
              children
            ) : (
              <React.Fragment>
                <Button
                  type="icon"
                  icon="caret-left"
                  size="2x"
                  className="page-button previous"
                  a11yText="Previous Page"
                  onClick={moveLeft}
                />

                {
                  pages[0] /* we paginate by re-ordering the array, so always display the first one */
                }

                <Button
                  type="icon"
                  icon="caret-right"
                  size="2x"
                  className="page-button next"
                  a11yText="Next Page"
                  onClick={moveRight}
                />
              </React.Fragment>
            )}
          </div>
          {hasFooter && (
            <div className="modal-footer">
              <div className="footer-group-secondary">{secondaryFooterGroup}</div>
              <div className="footer-group-primary">{primaryFooterGroup}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Modal;
